import * as React from "react";
import { useState, useCallback, useEffect } from 'react';
import {
    Edit,
    FormWithRedirect,
    Toolbar,
    SaveButton,
    TextInput,
    NumberInput,
    DateInput,
    List,
    Datagrid,
    TextField,
    NumberField,
    Create,
    SimpleForm,
    BooleanInput,
    BooleanField,
    ReferenceInput,
    ReferenceField,
    SelectInput,
    Filter,
    useDataProvider,
    useNotify,
    useRefresh,
    Button,
} from 'react-admin';
import { Typography, Box, Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';

const useStyles = makeStyles({
    deleteButton: { color: '#f44336 !important', backgroundColor: 'transparent !important' },
    button: { margin: '1em 0' },
    addRow: { display: 'flex', alignItems: 'center', gap: '1em', marginTop: '1em' },
});

const travelTypeChoices = [
    { id: 'paid', name: 'Paid' },
    { id: 'included', name: 'Included in day' },
];

const ProductionCalendarDaysTitle = props => {
    return <span>{props.record ? `${props.record.date}` : ''}</span>;
};

const ProductionCalendarDaysFilter = props => (
    <Filter {...props}>
        <ReferenceInput source="production_calendar_id" reference="production-calendars" label="Calendar" alwaysOn>
            <SelectInput optionText="name" />
        </ReferenceInput>
    </Filter>
);

// Locations panel — fetches, displays and manages locations for a calendar day
const CalendarDayLocationsPanel = ({ calendarId, dayId }) => {
    const classes = useStyles();
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const [locations, setLocations] = useState([]);
    const [locationId, setLocationId] = useState('');
    const [allLocations, setAllLocations] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchLocations = useCallback(() => {
        setLoading(true);
        dataProvider.getList('production-calendar-day-locations', {
            filter: { calendar_id: calendarId, day_id: dayId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'id', order: 'ASC' },
        }).then(({ data }) => {
            setLocations(data);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [calendarId, dayId, dataProvider]);

    const fetchAllLocations = useCallback(() => {
        dataProvider.getList('locations', {
            filter: {},
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
        }).then(({ data }) => {
            setAllLocations(data);
        });
    }, [dataProvider]);

    useEffect(() => {
        fetchLocations();
        fetchAllLocations();
    }, [calendarId, dayId]);

    const removeLocation = (locId) => {
        dataProvider.delete(
            `production-calendars/${calendarId}/days/${dayId}/locations`,
            { id: locId }
        ).then(() => {
            notify('Location removed successfully', 'success');
            fetchLocations();
        }).catch((response) => {
            notify(response.body?.message || 'Error removing location', 'error');
        });
    };

    const addLocation = () => {
        if (!locationId) {
            notify('Please select a location', 'warning');
            return;
        }
        dataProvider.create(
            `production-calendars/${calendarId}/days/${dayId}/locations`,
            { data: { location_id: parseInt(locationId) } }
        ).then(() => {
            notify('Location added successfully', 'success');
            setExpanded(false);
            setLocationId('');
            fetchLocations();
        }).catch((response) => {
            notify(response.body?.message || 'Error adding location', 'error');
        });
    };

    if (loading) return <Typography>Loading locations...</Typography>;

    return (
        <>
            {locations.length === 0 ? (
                <Typography variant="body2" color="textSecondary">No locations assigned</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {locations.map(loc => (
                            <TableRow key={loc.id}>
                                <TableCell>{loc.name}</TableCell>
                                <TableCell>
                                    <Button
                                        className={classes.deleteButton}
                                        size="small"
                                        label="Remove"
                                        onClick={(e) => { e.stopPropagation(); removeLocation(loc.id); }}
                                    >
                                        <DeleteIcon />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Button
                className={classes.button}
                variant="contained"
                label="Add Location"
                onClick={() => setExpanded(!expanded)}
            />

            {expanded && (
                <Box className={classes.addRow}>
                    <select
                        value={locationId}
                        onChange={(e) => setLocationId(e.target.value)}
                        style={{ padding: '8px', minWidth: '200px' }}
                    >
                        <option value="">Select a location...</option>
                        {allLocations.map(loc => (
                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                    </select>
                    <Button variant="contained" label="Add" onClick={addLocation} />
                    <Button label="Cancel" onClick={() => { setExpanded(false); setLocationId(''); }} />
                </Box>
            )}
        </>
    );
};

export const ProductionCalendarDaysList = props => (
    <List {...props} filters={<ProductionCalendarDaysFilter />}>
        <Datagrid rowClick="edit">
            <TextField source="production_calendar_id" label="Calendar" />
            <TextField source="date" />
            <NumberField source="block" />
            <NumberField source="block_day_no" label="Day No." />
            <ReferenceField source="day_type_id" reference="day-types" label="Day Type" link={false}>
                <TextField source="name" />
            </ReferenceField>
            <ReferenceField source="shoot_day_type_id" reference="shoot-day-types" label="Shoot Day Type" link={false}>
                <TextField source="name" />
            </ReferenceField>
            <BooleanField source="night_shoot" label="Night Shoot" />
            <TextField source="unit_call" label="Unit Call" />
            <TextField source="unit_wrap" label="Unit Wrap" />
            <NumberField source="meal_length" label="Meal Length (mins)" />
            <NumberField source="delayed_meal" label="Delayed Meal (mins)" />
            <NumberField source="curtailed_meal" label="Curtailed Meal (mins)" />
            <TextField source="status" label="Status" />
        </Datagrid>
    </List>
);

export const ProductionCalendarDaysEdit = props => (
    <Edit {...props} title={<ProductionCalendarDaysTitle />} undoable={false}>
        <FormWithRedirect
            render={formProps => (
                <Box p="1em">
                    <Box display="flex">

                        {/* Left column — form fields */}
                        <Box flex={1} mr="1em">
                            <Typography variant="h6" gutterBottom>Day Details</Typography>
                            <form>
                                <ReferenceInput source="production_calendar_id" reference="production-calendars" label="Calendar" fullWidth>
                                    <SelectInput optionText="name" />
                                </ReferenceInput>
                                <DateInput source="date" label="Date" fullWidth />
                                <NumberInput source="block" min={1} allowEmpty fullWidth />
                                <NumberInput source="block_day_no" label="Day No." min={1} allowEmpty fullWidth />
                                <ReferenceInput
                                    source="day_type_id"
                                    reference="day-types"
                                    label="Day Type"
                                    filter={{ show_on_calendar: true }}
                                    fullWidth
                                >
                                    <SelectInput optionText="name" />
                                </ReferenceInput>
                                <ReferenceInput source="shoot_day_type_id" reference="shoot-day-types" label="Shoot Day Type" allowEmpty fullWidth>
                                    <SelectInput optionText="name" />
                                </ReferenceInput>
                                <BooleanInput source="night_shoot" label="Night Shoot" />
                                <SelectInput source="night_work_resolution" label="Night Work Resolution" choices={[
                                    { id: 'paid', name: 'Paid' },
                                    { id: 'rest', name: 'Accrues Rest' },
                                ]} allowEmpty fullWidth />
                                <TextInput source="travel_in" label="Travel In (HH:MM)" fullWidth />
                                <SelectInput source="travel_in_type" label="Travel In Type" choices={travelTypeChoices} allowEmpty fullWidth />
                                <TextInput source="unit_call" label="Unit Call (HH:MM)" fullWidth />
                                <TextInput source="meal_start" label="Meal Start (HH:MM)" fullWidth />
                                <TextInput source="meal_end" label="Meal End (HH:MM)" fullWidth />
                                <TextInput source="unit_wrap" label="Unit Wrap (HH:MM)" fullWidth />
                                <TextInput source="travel_out" label="Travel Out (HH:MM)" fullWidth />
                                <SelectInput source="travel_out_type" label="Travel Out Type" choices={travelTypeChoices} allowEmpty fullWidth />
                                <NumberInput source="delayed_meal" label="Delayed Meal (mins)" min={0} fullWidth />
                                <BooleanInput source="delayed_meal_overridden" label="Override Delayed Meal" />
                                <NumberInput source="curtailed_meal" label="Curtailed Meal (mins)" min={0} fullWidth />
                                <BooleanInput source="curtailed_meal_overridden" label="Override Curtailed Meal" />
                                <SelectInput source="status" label="Status" choices={[
                                    { id: 'scheduled', name: 'Scheduled' },
                                    { id: 'complete', name: 'Complete' },
                                ]} fullWidth />
                                <Toolbar disableGutters={true}>
                                    <SaveButton
                                        saving={formProps.saving}
                                        handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                                    />
                                </Toolbar>
                            </form>
                        </Box>

                        {/* Right column — locations */}
                        <Box flex={1} ml="1em">
                            <Typography variant="h6" gutterBottom>Locations</Typography>
                            <CalendarDayLocationsPanel
                                calendarId={formProps.record.production_calendar_id}
                                dayId={formProps.record.id}
                            />
                        </Box>

                    </Box>
                </Box>
            )}
        />
    </Edit>
);

export const ProductionCalendarDaysCreate = props => (
    <Create {...props} title={<ProductionCalendarDaysTitle />} undoable={false} redirect="list">
        <SimpleForm redirect="list">
            <ReferenceInput source="production_calendar_id" reference="production-calendars" label="Calendar">
                <SelectInput optionText="name" />
            </ReferenceInput>
            <DateInput source="date" label="Date" />
            <NumberInput source="block" min={1} allowEmpty />
            <NumberInput source="block_day_no" label="Day No." min={1} allowEmpty />
            <ReferenceInput
                source="day_type_id"
                reference="day-types"
                label="Day Type"
                filter={{ show_on_calendar: true }}
            >
                <SelectInput optionText="name" />
            </ReferenceInput>
            <ReferenceInput source="shoot_day_type_id" reference="shoot-day-types" label="Shoot Day Type" allowEmpty>
                <SelectInput optionText="name" />
            </ReferenceInput>
            <BooleanInput source="night_shoot" label="Night Shoot" />
            <SelectInput source="night_work_resolution" label="Night Work Resolution" choices={[
                { id: 'paid', name: 'Paid' },
                { id: 'rest', name: 'Accrues Rest' },
            ]} allowEmpty fullWidth />
            <TextInput source="travel_in" label="Travel In (HH:MM)" />
            <SelectInput source="travel_in_type" label="Travel In Type" choices={travelTypeChoices} allowEmpty />
            <TextInput source="unit_call" label="Unit Call (HH:MM)" />
            <TextInput source="meal_start" label="Meal Start (HH:MM)" />
            <TextInput source="meal_end" label="Meal End (HH:MM)" />
            <TextInput source="unit_wrap" label="Unit Wrap (HH:MM)" />
            <TextInput source="travel_out" label="Travel Out (HH:MM)" />
            <SelectInput source="travel_out_type" label="Travel Out Type" choices={travelTypeChoices} allowEmpty />
            <NumberInput source="delayed_meal" label="Delayed Meal (mins)" min={0} />
            <BooleanInput source="delayed_meal_overridden" label="Override Delayed Meal" />
            <NumberInput source="curtailed_meal" label="Curtailed Meal (mins)" min={0} />
            <BooleanInput source="curtailed_meal_overridden" label="Override Curtailed Meal" />
            <SelectInput source="status" label="Status" choices={[
                { id: 'scheduled', name: 'Scheduled' },
                { id: 'complete', name: 'Complete' },
            ]} defaultValue="scheduled" />
        </SimpleForm>
    </Create>
);